import React, { useCallback, useEffect, useState } from "react";
import { getCsrfToken } from "../../../../../public/api/authApi";
import GlobalDataTable from "../../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../../global/GlobalBadge";
import GlobalFeedback from "../../../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";
import { GlobalModal } from "../../../../../global/GlobalModal";
import { GlobalButton } from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import PurposeForm from "./PurposeForm";
import {
  getVisitorPurposes,
  createVisitorPurpose,
  updateVisitorPurpose,
  deleteVisitorPurpose,
} from "../../../api/visitorPurposeApi";
import { useVisitorRole } from "../../../utils/useVisitorRole";
import { isVisitorMasterStatusActive } from "../../../utils/visitorMasterStatusOptions";
import { ensureCsrfToken, safeErrorText } from "../../../utils/visitorHelpers";

export default function PurposeMasterSection({ showInactive = false }) {
  const perms = useVisitorRole();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getVisitorPurposes();
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(showInactive ? list : list.filter((r) => Number(r.is_active) !== 2));
    } catch (err) {
      setError(safeErrorText(err, "Failed to load visit purposes"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (body) => {
    setSubmitting(true);
    setFormError("");
    try {
      const csrf = await ensureCsrfToken(getCsrfToken);
      const res = await createVisitorPurpose(body, csrf);
      setSuccessMsg(res?.message || "Visitor purpose created successfully");
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(safeErrorText(err));
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (body) => {
    if (!editing?.id) return;
    setSubmitting(true);
    setFormError("");
    try {
      const csrf = await ensureCsrfToken(getCsrfToken);
      const res = await updateVisitorPurpose(editing.id, body, csrf);
      setSuccessMsg(res?.message || "Visitor purpose updated successfully");
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(safeErrorText(err));
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const csrf = await ensureCsrfToken(getCsrfToken);
      const res = await deleteVisitorPurpose(deleteTarget.id, csrf);
      setSuccessMsg(res?.message || "Visitor purpose deleted successfully");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(safeErrorText(err, "Failed to delete purpose"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <p className="visitor-table-loading">Loading visit purposes…</p>;
  }

  return (
    <div className="ub-master-section">
      <GlobalFeedback type="error" text={error} role="alert" />
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />

      {perms.canManageMasters ? (
        <PurposeForm
          mode={editing ? "edit" : "create"}
          initialValues={
            editing
              ? {
                  purpose_name: editing.purpose_name ?? "",
                  status_id:
                    editing.status_id != null && editing.status_id !== ""
                      ? String(editing.status_id)
                      : "1",
                }
              : undefined
          }
          onSubmit={editing ? handleUpdate : handleCreate}
          onCancel={editing ? () => setEditing(null) : undefined}
          submitting={submitting}
          serverError={formError}
        />
      ) : (
        <p className="visitor-workspace__placeholder" style={{ marginBottom: "1rem" }}>
          You have view-only access to visit purposes.
        </p>
      )}

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Visit purposes"
        headers={[
          { key: "purpose_name", label: "Purpose name" },
          { key: "status", label: "Status" },
          { key: "created_at", label: "Created" },
          { key: "actions", label: "Actions", className: "ub-table__col-actions" },
        ]}
      >
        {rows.map((row) => {
          const active = isVisitorMasterStatusActive(row);
          return (
            <tr key={row.id}>
              <td style={{ fontWeight: 500 }}>{row.purpose_name}</td>
              <td>
                <GlobalBadge active={active} activeText="ACTIVE" inactiveText="INACTIVE" />
              </td>
              <td>
                {row.created_at
                  ? new Date(row.created_at).toLocaleDateString()
                  : "—"}
              </td>
              <td className="ub-table__col-actions">
                {perms.canManageMasters ? (
                  <div className="ub-table-actions">
                    <GlobalTableActionButton action="edit" onClick={() => setEditing(row)} />
                    {perms.canDeleteMasters ? (
                      <GlobalTableActionButton action="delete" onClick={() => setDeleteTarget(row)} />
                    ) : null}
                  </div>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          );
        })}
        {rows.length === 0 ? (
          <tr>
            <td colSpan={4} style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
              No visit purposes found
            </td>
          </tr>
        ) : null}
      </GlobalDataTable>

      <GlobalModal
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete visit purpose"
        size="sm"
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete"}
            </GlobalButton>
          </div>
        }
      >
        <p>
          Soft-delete purpose <strong>{deleteTarget?.purpose_name}</strong>?
        </p>
      </GlobalModal>
    </div>
  );
}
